import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Globe, Loader2, CheckCircle } from 'lucide-react';
import { TIMEZONE_OPTION_GROUPS } from '../utils/timezoneOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TimezoneAuditDialog = ({ open, onOpenChange, token, onFixed }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [audit, setAudit] = useState(null);
  const [targetTz, setTargetTz] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/timezone-audit`, {
        headers: { Authorization: `Bearer ${token}` } });
      setAudit(data);
      setTargetTz((prev) => prev || data.company_timezone);
    } catch (e) {
      toast({ title: 'Audit failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const fixAll = async () => {
    if (!audit?.flagged?.length || !targetTz) return;
    const mismatched = audit.flagged.filter((u) => u.issue === 'mismatch').length;
    const msg = mismatched > 0
      ? `Set ${audit.flagged.length} worker(s) to ${targetTz}? ${mismatched} of them already have a DIFFERENT timezone set — only do this if they all actually work in ${targetTz}.`
      : `Set ${audit.flagged.length} worker(s) with no timezone to ${targetTz}?`;
    if (!window.confirm(msg)) return;
    setFixing(true);
    try {
      const { data } = await axios.post(`${API}/admin/timezone-audit/fix`,
        { timezone: targetTz, user_ids: audit.flagged.map((u) => u.id) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Timezones updated', description: `${data.updated} worker(s) set to ${targetTz}` });
      onFixed?.();
      load();
    } catch (e) {
      toast({ title: 'Fix failed', description: e.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setFixing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="timezone-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe className="w-5 h-5" /> Timezone Audit</DialogTitle>
          <DialogDescription>
            A wrong or missing timezone can put a night shift on the wrong payroll day.
            Company default: <span className="font-semibold">{audit?.company_timezone || '…'}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" /></div>
        ) : audit && audit.flagged_count === 0 ? (
          <div className="py-8 text-center" data-testid="timezone-audit-all-clear">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
            <p className="font-semibold">All {audit.total_workers} workers have the company timezone</p>
            <p className="text-sm text-muted-foreground">Payroll days are bucketed correctly.</p>
          </div>
        ) : audit ? (
          <div className="space-y-4">
            <p className="text-sm font-medium" data-testid="timezone-audit-flagged-count">
              {audit.flagged_count} of {audit.total_workers} worker(s) need attention:
            </p>
            <div className="max-h-64 overflow-y-auto rounded-lg border divide-y">
              {audit.flagged.map((u) => (
                <div key={u.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`tz-audit-row-${u.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${
                    u.issue === 'missing' ? 'bg-red-500/15 text-red-500' : 'bg-amber-500/15 text-amber-500'}`}>
                    {u.issue === 'missing' ? 'No timezone' : u.timezone}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]">
                <p className="text-xs font-medium mb-1">Set all flagged workers to:</p>
                <Select value={targetTz} onValueChange={setTargetTz}>
                  <SelectTrigger data-testid="tz-audit-target-select"><SelectValue placeholder="Pick a timezone" /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONE_OPTION_GROUPS.map((g) => (
                      <React.Fragment key={g.label}>
                        {g.options.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} data-testid={`tz-audit-option-${opt.value}`}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fixAll} disabled={fixing || !targetTz} data-testid="tz-audit-fix-all-btn">
                {fixing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Fix all {audit.flagged_count}
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default TimezoneAuditDialog;
